import {
  COUNTRY_CODES,
  mapSubdivisionReplacedBy,
} from '@wix/form-multiline-address';
import { getCountryByShortKey } from '@wix/locale-dataset-javascript';
import React, { type ReactNode, useCallback, useMemo, useRef } from 'react';
import {
  Select as SelectRAC,
  Button as ButtonRAC,
  SelectValue as SelectValueRAC,
  Popover as PopoverRAC,
  ListBox as ListBoxRAC,
  ListBoxItem as ListBoxItemRAC,
} from 'react-aria-components';

import { useFocusFieldEvent } from '../../../contexts/events-context';
import { useFieldAttributes } from '../../../contexts/field-attributes-context';
import { useFieldPropsV2 } from '../../../contexts/field-props-context';
import { useTranslation } from '../../../contexts/translation-context';
import { type MlaSubdivisionProps } from '../../../fields/mla-subdivision/mla-subdivision/mla-subdivision-wds';
import { useCountryCode } from '../../../fields/multiline-address/multiline-address/country-code-context';
import { useAddressLabel } from '../../../hooks/use-address-label';
import {
  applyInternalProps,
  findChildOfType,
} from '../../../services/find-child';
import { Description } from '../../field-description/field-description-headless';
import { Error } from '../../field-error/field-error-headless';
import {
  Label,
  type LabelInternalProps,
} from '../../field-label/field-label-headless';

export type SubdivisionProps = {
  children: ReactNode;
};

export const Subdivision = ({ children }: SubdivisionProps) => {
  const {
    id,
    value,
    onChange,
    disabled,
    required,
    onBlur,
    onFocus,
    FieldLayout,
  } = useFieldPropsV2<MlaSubdivisionProps>();

  const { t } = useTranslation();
  const countryCode = useCountryCode();
  const countryCodeAlpha3 = countryCode
    ? COUNTRY_CODES[countryCode]
    : undefined;

  const country = countryCode && getCountryByShortKey(countryCode);

  const subdivisionOptions = useMemo(() => {
    const subdivisions = mapSubdivisionReplacedBy(
      country?.subdivisions?.list || [],
      countryCodeAlpha3!,
    );
    return (subdivisions || [])
      .map((subdivision) => ({
        value: subdivision.key,
        label: t(subdivision.displayName),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [countryCodeAlpha3, t, country]);

  const handleChange = useCallback(
    (selectedValue: React.Key | null) => {
      onChange(typeof selectedValue === 'string' ? selectedValue : '');
    },
    [onChange],
  );

  const subdivisionLabel = useAddressLabel('subdivision');
  const labelElement = findChildOfType(children, Subdivision.Label);
  const triggerElement = findChildOfType(children, Subdivision.Trigger);
  const optionsElement = findChildOfType(children, Subdivision.Options);
  const descriptionElement = findChildOfType(children, Subdivision.Description);
  const errorElement = findChildOfType(children, Subdivision.Error);

  return (
    <FieldLayout
      fieldId={id}
      renderLabel={() =>
        applyInternalProps<LabelInternalProps>(labelElement, {
          label: subdivisionLabel,
        })
      }
      renderInput={() => (
        <SelectRAC
          value={value ?? null}
          onChange={handleChange}
          isDisabled={disabled}
          isRequired={required}
          onBlur={onBlur}
          onFocus={onFocus}
        >
          {triggerElement}
          {applyInternalProps<OptionsInternalProps>(optionsElement, {
            options: subdivisionOptions,
          })}
        </SelectRAC>
      )}
      renderDescription={() => (
        <>
          {descriptionElement}
          {errorElement}
        </>
      )}
    />
  );
};

type TriggerProps = {
  className?: string;
};

const Trigger = ({ className }: TriggerProps) => {
  const { inputId, ariaDescribedBy } = useFieldAttributes();
  const { id } = useFieldPropsV2<MlaSubdivisionProps>();

  const ref = useRef<HTMLButtonElement>(null);
  useFocusFieldEvent(() => {
    ref.current?.focus();
  }, id);

  return (
    <ButtonRAC
      ref={ref}
      id={inputId}
      aria-describedby={ariaDescribedBy}
      className={className}
    >
      <SelectValueRAC />
    </ButtonRAC>
  );
};

type OptionsProps = {
  children: ReactNode;
  className?: string;
};

type OptionsInternalProps = {
  options: { value: string; label: string }[];
};

const Options = ({ children, className, ...internal }: OptionsProps) => {
  const { options } = internal as OptionsInternalProps;

  const optionElement = findChildOfType(children, Options.Option);

  return (
    <PopoverRAC className={className}>
      <ListBoxRAC>
        {options.map((option) =>
          applyInternalProps<OptionInternalProps>(optionElement, {
            key: option.value,
            value: option.value,
            label: option.label,
          }),
        )}
      </ListBoxRAC>
    </PopoverRAC>
  );
};

type OptionProps = {
  className?: string;
};

type OptionInternalProps = {
  value: string;
  label: string;
};

const Option = ({ className, ...internal }: OptionProps) => {
  const { value, label } = internal as OptionInternalProps;
  return (
    <ListBoxItemRAC className={className} id={value} textValue={label}>
      {label}
    </ListBoxItemRAC>
  );
};

Options.Option = Option;

Subdivision.Label = Label;
Subdivision.Trigger = Trigger;
Subdivision.Options = Options;
Subdivision.Description = Description;
Subdivision.Error = Error;
